import { Fragment, forwardRef } from 'react'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { Separator } from '@/components/ui/separator'
import type { RemapExperienceForViewOnly } from '@/features/experience-view-only/types'
import { useTranslation } from '@/translations/i18n'
import { AnswerField, Content } from './atoms'

export const MerchandisingStep = forwardRef<
	HTMLDivElement,
	{ experience: RemapExperienceForViewOnly }
>(({ experience }, ref) => {
	const { t } = useTranslation()

	return (
		<div className='flex w-full flex-col' ref={ref}>
			<Content
				sectionTitle={t(
					'vetting:merch.experience.input_shell_form_title'
				)}
				title={t('experience_shell:step_merchandising')}
			>
				<AnswerField
					hasValue={!!experience.host.firstName}
					labelTitle={t('vetting:merch.experience.first_name_label')}
					value={experience.host.firstName ?? undefined}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.expertise_summary?.answer
					}
					labelTitle={t('experience_view_only:bio')}
					value={
						experience.expertise.answers.expertise_summary?.answer
					}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.recognition_summary
							?.answer
					}
					labelTitle={t(
						'vetting:merch.experience.recognition_general_label'
					)}
					value={
						experience.expertise.answers.recognition_summary?.answer
					}
				/>
			</Content>

			<Separator orientation='horizontal' />

			<Content>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.listing_title?.answer
					}
					labelTitle={t(
						'vetting:merch.experience.listing_title_label'
					)}
					value={experience.expertise.answers.listing_title?.answer}
				/>
				<AnswerField
					hasValue={!!experience.expertise.answers.byline?.answer}
					labelTitle={t('vetting:merch.experience.byline_label')}
					value={experience.expertise.answers.byline?.answer}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.location_description
							?.answer
					}
					labelTitle={t(
						'vetting:merch.experience.location_description_label'
					)}
					value={
						experience.expertise.answers.location_description
							?.answer
					}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.guest_activities?.answer
					}
					labelTitle={t(
						'vetting:merch.experience.what_guest_will_do_label'
					)}
					value={
						experience.expertise.answers.guest_activities?.answer
					}
				/>
			</Content>

			<Content
				columns={2}
				sectionTitle={t('experience_view_only:content_sent_to_host')}
			>
				{/* expertise title */}
				<AnswerField
					hasValue={
						!!experience.expertise.answers.host_experience_title
							?.answer
					}
					labelTitle={t('experience_view_only:title')}
					value={
						experience.expertise.answers.host_experience_title
							?.answer
					}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.improved_experience_title
							?.answer
					}
					labelTitle={t('experience_view_only:improved_title')}
					value={
						experience.expertise.answers.improved_experience_title
							?.answer
					}
				/>

				{/* expertise description (bio) */}
				<AnswerField
					hasValue={
						!!experience.expertise.answers
							.host_expertise_description?.answer
					}
					labelTitle={t('experience_view_only:bio')}
					value={
						experience.expertise.answers.host_expertise_description
							?.answer
					}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers
							.improved_host_expertise_description?.answer
					}
					labelTitle={t('experience_view_only:improved_bio')}
					value={
						experience.expertise.answers
							.improved_host_expertise_description?.answer
					}
				/>

				{/* recognition title */}
				<AnswerField
					hasValue={
						!!experience.expertise.answers.host_recognition_title
							?.answer
					}
					labelTitle={t(
						'vetting:merch.experience.recognition_title_label'
					)}
					value={
						experience.expertise.answers.host_recognition_title
							?.answer
					}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers
							.improved_host_recognition_title?.answer
					}
					labelTitle={t('experience_view_only:improved_recognition')}
					value={
						experience.expertise.answers
							.improved_host_recognition_title?.answer
					}
				/>

				{/* listing title */}
				<AnswerField
					hasValue={
						!!experience.expertise.answers.experience_title?.answer
					}
					labelTitle={t(
						'vetting:merch.experience.listing_title_label'
					)}
					value={
						experience.expertise.answers.experience_title?.answer
					}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.improved_experience_title
							?.answer
					}
					labelTitle={t(
						'experience_view_only:improved_listing_title'
					)}
					value={
						experience.expertise.answers.improved_experience_title
							?.answer
					}
				/>

				{/* listing description */}

				<AnswerField
					hasValue={
						!!experience.expertise.answers.experience_description
							?.answer
					}
					labelTitle={t(
						'vetting:merch.experience.listing_description_label'
					)}
					value={
						experience.expertise.answers.experience_description
							?.answer
					}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers
							.improved_experience_description?.answer
					}
					labelTitle={t(
						'experience_view_only:improved_listing_description'
					)}
					value={
						experience.expertise.answers
							.improved_experience_description?.answer
					}
				/>
				<Separator orientation='horizontal' />
				<Separator orientation='horizontal' />
			</Content>

			{experience.itineraries.map((itinerary, index) => (
				<Fragment key={itinerary.id}>
					<Content columns={1}>
						<p className='font-medium text-base text-neutral-900'>
							{t(
								'vetting:merch.experience.activity_input_label',
								{ index: index + 1 }
							)}
						</p>

						<GalleryImageItem
							className='h-16 w-16'
							src={itinerary?.coverImage?.url ?? ''}
						/>
					</Content>

					<Content columns={2}>
						{/* title */}
						<AnswerField
							hasValue={!!itinerary.answers.title?.answer}
							labelTitle={t('experience_view_only:title')}
							value={itinerary.answers.title?.answer}
						/>
						<AnswerField
							hasValue={
								!!itinerary.answers.improved_title?.answer
							}
							labelTitle={t(
								'experience_view_only:improved_title'
							)}
							value={itinerary.answers.improved_title?.answer}
						/>

						{/* description */}
						<AnswerField
							hasValue={!!itinerary.answers.description?.answer}
							labelTitle={t('experience_view_only:description')}
							value={itinerary.answers.description?.answer}
						/>
						<AnswerField
							hasValue={
								!!itinerary.answers.improved_description?.answer
							}
							labelTitle={t(
								'experience_view_only:improved_description'
							)}
							value={
								itinerary.answers.improved_description?.answer
							}
						/>

						{/* duration */}
						<AnswerField
							hasValue={!!itinerary.answers.duration?.answer}
							labelTitle={t('experience_view_only:duration')}
							value={t('experience_view_only:mins', {
								count: itinerary.answers.duration?.answer
							})}
						/>
					</Content>
				</Fragment>
			))}
		</div>
	)
})

MerchandisingStep.displayName = 'MerchandisingStep'
